import React, { useCallback, useMemo, useState } from "react";

import { TOOLTIP_PRIORITY, TooltipKey } from "@/hooks/onboardingTooltipConstants";
import { TooltipManagerContext } from "@/hooks/TooltipManagerContext";

/**
 * Provider component for the tooltip manager.
 * Ensures only one onboarding tooltip shows at a time based on priority.
 */
export function TooltipManagerProvider({ children }: { children: React.ReactNode }) {
  // Set of tooltips that want to show
  const [wantToShow, setWantToShow] = useState<Set<TooltipKey>>(new Set());

  // Calculate which tooltip is actually active based on priority
  const activeTooltip = useMemo(() => {
    for (const key of TOOLTIP_PRIORITY) {
      if (wantToShow.has(key)) {
        return key;
      }
    }
    return null;
  }, [wantToShow]);

  const registerTooltip = useCallback((key: TooltipKey) => {
    setWantToShow((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const unregisterTooltip = useCallback((key: TooltipKey) => {
    setWantToShow((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const isTooltipVisible = useCallback(
    (key: TooltipKey) => {
      return activeTooltip === key;
    },
    [activeTooltip]
  );

  const value = useMemo(
    () => ({
      activeTooltip,
      registerTooltip,
      unregisterTooltip,
      isTooltipVisible,
    }),
    [activeTooltip, registerTooltip, unregisterTooltip, isTooltipVisible]
  );

  return <TooltipManagerContext.Provider value={value}>{children}</TooltipManagerContext.Provider>;
}
